import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  MessageCircle,
  BrainCircuit,
  AudioLines,
  Globe,
  Smartphone,
  Cloud,
  Workflow,
  Database,
} from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1644325349124-d1756b79dd42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG5ldXJhbCUyMG5ldHdvcmslMjBub2RlfGVufDB8fHx8MTc4NzQ2ODg5Nnww&ixlib=rb-4.1.0&q=85";

const CHIPS = [
  { icon: MessageCircle, label: "WhatsApp AI", className: "left-[6%] top-[14%]", delay: 0 },
  { icon: AudioLines, label: "Voice Agents", className: "right-[4%] top-[8%]", delay: 0.8 },
  { icon: Workflow, label: "Automation", className: "left-[10%] bottom-[24%]", delay: 1.6 },
  { icon: Globe, label: "Websites", className: "right-[8%] top-[42%]", delay: 0.4 },
  { icon: Smartphone, label: "Apps", className: "left-[38%] top-[4%]", delay: 1.2 },
  { icon: Cloud, label: "Cloud", className: "right-[18%] bottom-[10%]", delay: 2.0 },
  { icon: Database, label: "Data", className: "left-[2%] top-[52%]", delay: 2.4 },
  { icon: BrainCircuit, label: "AI Core", className: "right-[42%] bottom-[4%]", delay: 0.6 },
];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ index, children, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const visualY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="noise-overlay relative overflow-hidden bg-navy-950 bg-grid-dark pb-24 pt-36 md:pb-32 md:pt-44"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-electric/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 h-[320px] w-[420px] rounded-full bg-cyan-500/10 blur-[120px]"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-[1.05fr_0.95fr]">
        <motion.div style={{ y: textY }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-electric-bright backdrop-blur-sm"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
            AI · Automation · Digital Solutions — UK & India
          </motion.p>

          <h1 className="mt-7 font-display text-5xl font-semibold leading-[1.04] tracking-tighter text-white sm:text-6xl lg:text-7xl">
            <MaskedLine index={0}>Build Smarter.</MaskedLine>
            <MaskedLine index={1}>
              <span className="text-gradient-blue">Automate Faster.</span>
            </MaskedLine>
            <MaskedLine index={2}>Grow Better.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
            data-testid="hero-subtext"
          >
            AI, automation and digital solutions built around the way your business
            works. Prata Technologies helps you transform manual processes into
            intelligent digital workflows.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-book-consultation-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-electric px-8 py-4 font-medium text-white transition-[background-color,box-shadow,transform] duration-300 hover:scale-[1.02] hover:bg-electric-bright hover:shadow-[0_0_28px_rgba(37,99,235,0.5)] active:scale-95"
            >
              Book a Free Consultation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-transparent px-8 py-4 font-medium text-white backdrop-blur-sm transition-[border-color,background-color] duration-300 hover:border-white/50 hover:bg-white/5"
            >
              Explore Our Services
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: visualY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden h-[520px] lg:block"
          data-testid="hero-visual"
        >
          <div className="absolute inset-6 overflow-hidden rounded-3xl border border-white/10 shadow-[0_40px_120px_-20px_rgba(37,99,235,0.35)]">
            <img
              src={HERO_IMAGE}
              alt="Abstract AI network visualisation representing business automation"
              className="h-full w-full object-cover opacity-80"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/30 to-transparent" />
          </div>
          {CHIPS.map((chip) => (
            <motion.div
              key={chip.label}
              className={`glass-chip absolute flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-slate-200 ${chip.className}`}
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: chip.delay }}
            >
              <chip.icon className="h-3.5 w-3.5 text-cyan-400" strokeWidth={1.5} />
              {chip.label}
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
